/**
 * Boosts — the characteristics a character's own spells put on their sheet.
 *
 * A boost is a spell that buffs its caster; what the build cares about is not
 * the cast but the line it leaves on the sheet. Maîtrise d'Arme is the common
 * one: +100/200/300 Puissance (armes) by grade, +120/240/360 on a critical
 * cast, for three turns. Every class has its own (Tirs Puissants, Furie, the
 * Zobal masks, ...), and a character can only cast its own class's — so a
 * boost carries `breed_id` (null for a common spell) and a class-less caller
 * sees the common ones only.
 *
 * Which spells are boosts is read from the game client, not written here — see
 * `backend/core/services/dofus_local/boosts.py` and
 * `manage.py export_frontend_boosts`, which writes `data/boosts.json`. A patch
 * that retunes the numbers reaches this file through the next sync.
 *
 * **What a saved build stores is option *keys*, never values.** A build made at
 * level 200 and reopened at level 100 gets grade 2's numbers, and a patch that
 * changes them changes what the build shows, both of which are what a player
 * would see in game.
 *
 * A boost can hold several buffs, each its own *group*: the Dofus Ébène grants
 * "+2 % distance after a melee attack" and "+2 % melee after a ranged attack",
 * each firing on its own and stacking 5 times. A group is picked on its own and
 * holds its own charges; an option carries its `group` (0 for a single-buff
 * boost, whose keys are unchanged).
 */

import BOOST_DATA from '../data/boosts.json';
import * as st from './st';

/** Every boost the export carries: class boosts by class, then common ones. */
export const BOOSTS = Array.isArray(BOOST_DATA?.boosts) ? BOOST_DATA.boosts : [];

const BOOST_BY_KEY = new Map(BOOSTS.map((boost) => [boost.key, boost]));

const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** True for an item's passive: it applies while the item is worn. */
export const isItemBoost = (boost) => boost?.item_id != null;

/** True for a common spell, which every class can cast. */
export const isCommonBoost = (boost) => !isItemBoost(boost)
    && (boost?.breed_id === null || boost?.breed_id === undefined);

/**
 * Whether a character of this class (its spell type id, `spellUtils.getTypeId`)
 * wearing these items (`equippedItemIds`, a Set of ids) has this boost: its
 * own class's, the common ones, and the passives of what it wears.
 */
export const boostAvailableTo = (boost, breedId, equippedItemIds = null) => {
    if (isItemBoost(boost)) return !!equippedItemIds?.has?.(toNum(boost.item_id));
    return isCommonBoost(boost) || toNum(boost.breed_id) === toNum(breedId);
};

/**
 * A map keyed by boost key (a selection, or charges) without the boosts of
 * another class. Common boosts and item passives are kept: a class change
 * leaves both where they were.
 */
export function withoutOtherClassBoosts(byKey, breedId) {
    return Object.fromEntries(Object.entries(byKey || {}).filter(([boostKey]) => {
        const boost = boostByKey(boostKey);
        return isItemBoost(boost) || boostAvailableTo(boost, breedId);
    }));
}

/**
 * Items whose passive boost is picked and worn. Their fixed default passive
 * (`item.passive_effects`, the Turquoise's 7 %) gives way to the picked one,
 * or the sheet would count the passive twice.
 */
export function pickedItemBoostIds(selection, equippedItemIds) {
    const out = new Set();
    for (const boostKey of Object.keys(normalizeBoostSelection(selection))) {
        const boost = boostByKey(boostKey);
        if (isItemBoost(boost) && boostAvailableTo(boost, null, equippedItemIds)) out.add(toNum(boost.item_id));
    }
    return out;
}

/** The boost a key names, or null. */
export const boostByKey = (key) => BOOST_BY_KEY.get(key) || null;

/**
 * The grade a character of this level would cast — the highest one they have.
 *
 * Null below the first grade's requirement: the spell is not learnable yet, so
 * the card has values to show but nothing to select.
 */
export function gradeForLevel(boost, lvl) {
    const level = toNum(lvl);
    let best = null;
    for (const grade of boost?.grades || []) {
        if (toNum(grade.level) > level) continue;
        if (!best || toNum(grade.grade) > toNum(best.grade)) best = grade;
    }
    return best;
}

/** First grade, for the level requirement a locked card quotes. */
export const firstGrade = (boost) => (boost?.grades || [])[0] || null;

/** Option keys a boost can ever offer, across every grade. */
function knownOptionKeys(boost) {
    const out = new Set();
    for (const grade of boost?.grades || []) {
        for (const option of grade.options || []) out.add(option.key);
    }
    return out;
}

/**
 * One option of a boost by key, from whichever grade carries it.
 *
 * Keys look parseable ("103-crit") and are deliberately not parsed: the export
 * owns their shape, and reading the option itself is what keeps this side from
 * having an opinion about it.
 */
function optionByKey(boost, key) {
    for (const grade of boost?.grades || []) {
        const found = (grade.options || []).find((option) => option.key === key);
        if (found) return found;
    }
    return null;
}

/**
 * A stored selection reduced to what this build of the app understands.
 *
 * Unknown boosts and unknown option keys are dropped; a selection that is still
 * *above the build's level* is kept, because level is a slider and dropping the
 * choice on the way past 30 would lose it silently. `boostEffectsMap` is where
 * an unreachable grade stops counting.
 */
export function normalizeBoostSelection(raw) {
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    for (const [boostKey, value] of Object.entries(raw)) {
        const boost = boostByKey(boostKey);
        if (!boost) continue;
        const known = knownOptionKeys(boost);
        const keys = (Array.isArray(value) ? value : [value])
            .filter((key) => typeof key === 'string' && known.has(key));
        if (keys.length) out[boostKey] = [...new Set(keys)];
    }
    return out;
}

/** The group an option belongs to: 0 unless the boost holds several buffs. */
const optionGroup = (option) => toNum(option?.group);

/**
 * Selection with this boost's choice replaced wholesale. An empty list clears it.
 *
 * The casting is a property of the pick, not a second pick, so the card sets
 * the whole thing rather than flipping one line. Options naming *different*
 * characteristics, or belonging to different groups, stack (a boost granting
 * two lines is two things the cast gives you); of two naming the *same* one in
 * one group, the last wins, because they are that line cast twice: a Maîtrise
 * d'Arme that both crit and did not is not a build, it is 220 Power.
 */
export function setBoostOptions(selection, boostKey, optionKeys) {
    const current = normalizeBoostSelection(selection);
    const boost = boostByKey(boostKey);
    if (!boost) return current;

    const byLine = new Map();
    for (const key of optionKeys || []) {
        const option = optionByKey(boost, key);
        if (!option) continue;
        byLine.set(`${optionGroup(option)}:${toNum(option.characteristic)}`, key);
    }
    const next = [...byLine.values()];
    const out = { ...current };
    if (next.length) out[boostKey] = next;
    else delete out[boostKey];
    return out;
}

/** True when this boost is picked, whichever casting. */
export const isBoostSelected = (selection, boostKey) =>
    (selection?.[boostKey]?.length || 0) > 0;

/** The picked option keys of one group of a boost. */
export function selectedGroupKeys(selection, boostKey, group) {
    const boost = boostByKey(boostKey);
    return (selection?.[boostKey] || [])
        .filter((key) => optionGroup(optionByKey(boost, key)) === toNum(group));
}

/**
 * Whether the picked casting is the critical one — null when nothing is picked.
 *
 * That null is the signal the card needs: with the boost off there is no answer
 * in the build, so the crit toggle falls back to what the reader last asked to
 * see.
 */
export function selectedCastingIsCritical(selection, boostKey) {
    const boost = boostByKey(boostKey);
    const keys = selection?.[boostKey];
    if (!boost || !keys?.length) return null;
    return keys.every((key) => optionByKey(boost, key)?.is_critical === true);
}

/**
 * What a line grants at `n` charges: `n` times its value — or, for a ladder
 * of tiers (the Sacrieur's Souffrance 1 to 10, `stack_values`), tier `n`'s.
 */
export const valueAtCount = (value, stackValues, n) => (
    Array.isArray(stackValues) && stackValues.length
        ? toNum(stackValues[Math.max(1, Math.min(stackValues.length, n)) - 1])
        : toNum(value) * n
);

/** The most charges one group of a grade can hold at once — 1 for an ordinary cast. */
export const groupMaxCharges = (grade, group = 0) => Math.max(1, ...(grade?.options || [])
    .filter((option) => optionGroup(option) === toNum(group))
    .map((option) => Math.floor(toNum(option.max_stack)) || 1));

/**
 * Charges reduced to `{ boostKey: { group: n } }`, whole numbers of at least 1,
 * for boosts this build of the app knows. Absent means the most the group can
 * hold. A bare number — what builds stored before boosts had groups — is group
 * 0's. A count above the current grade's cap is kept, for the same reason a
 * selection above the level is: `boostEffectsMap` clamps it.
 */
export function normalizeBoostCharges(raw) {
    if (!raw || typeof raw !== 'object') return {};
    const out = {};
    for (const [boostKey, value] of Object.entries(raw)) {
        if (!boostByKey(boostKey)) continue;
        const byGroup = value && typeof value === 'object' ? value : { 0: value };
        const clean = {};
        for (const [group, count] of Object.entries(byGroup)) {
            const n = Math.floor(toNum(count));
            if (n >= 1) clean[String(toNum(group))] = n;
        }
        if (Object.keys(clean).length) out[boostKey] = clean;
    }
    return out;
}

/**
 * Charges with one group's count replaced. The default is the most the group
 * can hold, so a count at `max` (or no count) clears the stored one.
 */
export function setBoostCharges(charges, boostKey, n, max = null, group = 0) {
    const out = normalizeBoostCharges(charges);
    if (!boostByKey(boostKey)) return out;
    const count = Math.floor(toNum(n));
    const counts = { ...(out[boostKey] || {}) };
    const atDefault = !(count >= 1) || (toNum(max) >= 1 && count >= toNum(max));
    if (atDefault) delete counts[String(toNum(group))];
    else counts[String(toNum(group))] = count;
    if (Object.keys(counts).length) out[boostKey] = counts;
    else delete out[boostKey];
    return out;
}

/** Charges one group counts at this grade: the stored count, or the cap by default. */
export const chargesAtGrade = (charges, boostKey, grade, group = 0) => {
    const cap = groupMaxCharges(grade, group);
    const stored = normalizeBoostCharges({ [boostKey]: charges?.[boostKey] })[boostKey]?.[String(toNum(group))];
    return stored >= 1 ? Math.min(cap, stored) : cap;
};

/**
 * `{ characteristicId: points }` the selection is worth at this level, for
 * this class — each boost's lines times the charges it holds.
 *
 * Values come from the grade the level affords, so the same selection is worth
 * less on a lower-level build and nothing at all below the first grade. Another
 * class's boost is worth nothing, but stays selected, for the same reason a
 * level drop keeps it: switching class and back should not lose the choice.
 */
export function boostEffectsMap(selection, lvl, breedId = null, charges = {}, equippedItemIds = null) {
    const out = {};
    for (const [boostKey, keys] of Object.entries(normalizeBoostSelection(selection))) {
        const boost = boostByKey(boostKey);
        if (!boostAvailableTo(boost, breedId, equippedItemIds)) continue;
        const grade = gradeForLevel(boost, lvl);
        if (!grade) continue;
        for (const option of grade.options || []) {
            if (!keys.includes(option.key)) continue;
            const id = toNum(option.characteristic);
            if (!id) continue;
            const n = chargesAtGrade(charges, boostKey, grade, optionGroup(option));
            out[id] = (out[id] || 0) + valueAtCount(option.value, option.stack_values, n);
        }
    }
    return out;
}

/**
 * One detail row per characteristic each group of a grade grants: the ordinary
 * value, the critical one (null when the spell cannot crit), what fires it
 * (`triggerLabel`), the conditions gating it — the same condition objects
 * damage lines carry, so the Spells tab's labels apply unchanged — and how
 * many times it stacks. A locked card describes its first grade.
 */
function detailLines(grade) {
    const byLine = new Map();
    for (const option of grade?.options || []) {
        const id = toNum(option.characteristic);
        const group = optionGroup(option);
        const key = `${group}:${id}`;
        const line = byLine.get(key) || {
            key, group, characteristic: id, value: null, critValue: null, conditions: [],
            triggerLabel: null, maxCharges: groupMaxCharges(grade, group), maxValue: null,
            isLadder: false,
        };
        if (option.is_critical) {
            line.critValue = toNum(option.value);
        } else {
            line.value = toNum(option.value);
            if (Array.isArray(option.stack_values) && option.stack_values.length) {
                line.isLadder = true;
                line.maxValue = toNum(option.stack_values[option.stack_values.length - 1]);
            }
            line.conditions = Array.isArray(option.conditions) ? option.conditions : [];
            line.triggerLabel = option.trigger_label || null;
        }
        byLine.set(key, line);
    }
    return [...byLine.values()];
}

/**
 * The buffs a grade holds, one per group: its cap, what fires it and its
 * conditions, for a card that picks each on its own.
 */
function gradeGroups(grade) {
    const groups = new Map();
    for (const option of grade?.options || []) {
        const group = optionGroup(option);
        if (groups.has(group) || option.is_critical) continue;
        groups.set(group, {
            index: group,
            maxCharges: groupMaxCharges(grade, group),
            // A ladder's picker names its tiers ("Souffrance 3") rather than counting charges.
            stackLabel: Array.isArray(option.stack_values) ? (option.stack_label || null) : null,
            // Or by what each tier needs (Cabale: direct, through a portal).
            stackConditions: Array.isArray(option.stack_conditions) ? option.stack_conditions : null,
            isLadder: Array.isArray(option.stack_values),
            triggerLabel: option.trigger_label || null,
            conditions: Array.isArray(option.conditions) ? option.conditions : [],
        });
    }
    return [...groups.values()];
}

/**
 * What the Boosts tab renders: one row per boost this class can cast, already
 * resolved for `lvl` — the class's passives first (the game applies them on
 * its own), then its spells, then the passives of the items it wears, then the
 * common ones.
 *
 * `options` is empty exactly when the character cannot cast the spell yet, and
 * `requiredLevel` then says at what level it could — the card stays on screen
 * rather than vanishing, so the tab never reads as "there are no boosts".
 */
export function boostRowsForLevel(lvl, breedId = null, equippedItemIds = null) {
    const available = BOOSTS.filter((boost) => boostAvailableTo(boost, breedId, equippedItemIds));
    const classBoosts = available.filter((boost) => !isCommonBoost(boost) && !isItemBoost(boost));
    const ordered = [
        ...classBoosts.filter((boost) => boost.is_passive === true),
        ...classBoosts.filter((boost) => boost.is_passive !== true),
        ...available.filter(isItemBoost),
        ...available.filter(isCommonBoost),
    ];
    return ordered.map((boost) => {
        const grade = gradeForLevel(boost, lvl);
        const first = firstGrade(boost);
        const shownGrade = grade || first;
        return {
            key: boost.key,
            spellId: boost.spell_id,
            isCommon: isCommonBoost(boost),
            itemId: isItemBoost(boost) ? toNum(boost.item_id) : null,
            itemName: boost.item_name || null,
            isPassive: boost.is_passive === true,
            name: boost.name,
            description: boost.description,
            icon: boost.icon_url,
            grade: grade ? toNum(grade.grade) : null,
            duration: grade ? grade.duration : (first?.duration ?? null),
            apCost: grade ? grade.ap_cost : (first?.ap_cost ?? null),
            requiredLevel: grade ? null : toNum(first?.level),
            groups: gradeGroups(shownGrade),
            options: (grade?.options || []).map((option) => ({
                key: option.key,
                group: optionGroup(option),
                characteristic: toNum(option.characteristic),
                value: toNum(option.value),
                stackValues: Array.isArray(option.stack_values) ? option.stack_values.map(toNum) : null,
                isCritical: option.is_critical === true,
                statLabel: st.STATS.find((s) => s.id === toNum(option.characteristic))?.label || null,
            })),
            lines: detailLines(shownGrade),
        };
    });
}
